<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="flex flex-wrap gap-3 mb-4 items-end">
      <div>
        <label class="text-xs text-gray-500">{{ $t('smm.common.social_network') }}</label>
        <select v-model="filters.networkId" @change="load" class="block border rounded-lg p-2 mt-1 text-sm">
          <option value="">{{ $t('common.all') }}</option>
          <option v-for="n in networks" :key="n.id" :value="n.id">{{ n.name }}</option>
        </select>
      </div>
      <div>
        <label class="text-xs text-gray-500">{{ $t('smm.schedule.view_mode') }}</label>
        <select v-model="viewMode" @change="load" class="block border rounded-lg p-2 mt-1 text-sm">
          <option value="month">{{ $t('smm.schedule.mode_month') }}</option>
          <option value="day">{{ $t('smm.schedule.mode_day') }}</option>
        </select>
      </div>
      <div v-if="viewMode === 'day'">
        <label class="text-xs text-gray-500">{{ $t('common.date') }}</label>
        <input v-model="filters.date" type="date" @change="load" class="block border rounded-lg p-2 mt-1 text-sm" />
      </div>
      <div v-else>
        <label class="text-xs text-gray-500">{{ $t('common.month') }}</label>
        <input v-model="filters.month" type="month" @change="load" class="block border rounded-lg p-2 mt-1 text-sm" />
      </div>
    </div>

    <div class="bg-white shadow-md rounded-lg">
      <div class="p-4 border-b">
        <h2 class="text-xl font-semibold">{{ $t('smm.schedule.title') }}</h2>
        <p class="text-sm text-gray-500 mt-1" v-if="viewMode === 'month'">
          {{ $t('smm.schedule.month_description') }}
        </p>
        <p class="text-sm text-gray-500 mt-1" v-else>
          {{ $t('smm.schedule.day_description', { date: formatDateLabel(filters.date) }) }}
        </p>
        <p v-if="totalDays" class="text-xs text-gray-400 mt-1">
          {{ $t('smm.schedule.stats', { days: totalDays, pins: totalPins }) }}
        </p>
      </div>

      <div v-if="loading" class="p-8 text-center text-gray-500">{{ $t('common.loading') }}</div>
      <div v-else-if="!scheduleDays.length" class="p-8 text-center text-gray-500">
        {{ viewMode === 'day' ? $t('smm.schedule.empty_day') : $t('smm.schedule.empty_month') }}
        {{ $t('smm.schedule.empty_hint') }}
      </div>

      <div v-else class="p-4 space-y-6">
        <section v-for="day in scheduleDays" :key="day.id">
          <h3 class="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
            {{ formatDateLabel(day.scheduled_for) }}
            <span class="text-xs font-normal text-gray-400">
              ({{ day.content_items?.length ?? 0 }} {{ pinWord(day.content_items?.length ?? 0) }})
            </span>
          </h3>
          <p v-if="day.title" class="text-sm text-gray-800 mb-1">{{ day.title }}</p>
          <p v-if="day.notes" class="text-xs text-gray-500 mb-2">{{ day.notes }}</p>
          <div class="grid gap-2 sm:grid-cols-2">
            <button
              v-for="item in day.content_items"
              :key="item.id"
              @click="openItem(item)"
              class="text-left border rounded-lg p-3 hover:border-blue-400 hover:bg-blue-50/50 transition text-sm"
            >
              <span class="text-xs uppercase text-gray-400">{{ item.format || 'pin' }}</span>
              <p class="font-medium text-gray-900">{{ item.title }}</p>
              <p class="text-gray-600 line-clamp-2 mt-0.5">{{ item.brief }}</p>
              <span class="inline-block mt-1 text-xs px-1.5 py-0.5 rounded" :class="statusClass(item.status)">
                {{ statusLabel(item.status) }}
              </span>
            </button>
          </div>
        </section>
      </div>
    </div>

    <ai-generations-panel
      v-if="filters.networkId"
      :social-network-id="filters.networkId"
      type="schedule"
      class="mt-6"
    />

    <!-- Item popup -->
    <schedule-item-modal
      v-if="activeItem"
      :item="activeItem"
      :match="mediaMatch"
      :post-prompts="postPrompts"
      :generate-form="generateForm"
      :publish-form="publishForm"
      :pinterest-boards="pinterestBoards"
      :generating="generating"
      :publishing="publishing"
      :resolving="resolvingMedia"
      :ai-provider-label="aiProviderLabel"
      :is-pinterest="activeItem.social_network?.slug === 'pinterest'"
      :link-url="publishForm.link_url"
      @close="closeItem"
      @generate="generateContent"
      @publish="publishPinterest"
      @files="onFiles"
      @update:generate-form="generateForm = $event"
      @update:publish-form="publishForm = $event"
      @resolve="resolveMedia"
      @toggle-file="toggleMediaFile"
    />
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'
import AiGenerationsPanel from '@/components/smm/AiGenerationsPanel.vue'
import ScheduleItemModal from '@/components/smm/ScheduleItemModal.vue'

export default {
  components: {
    SmmSubNav,
    AiGenerationsPanel,
    ScheduleItemModal,
  },
  data: () => ({
    networks: [],
    scheduleDays: [],
    postPrompts: [],
    loading: true,
    generating: false,
    publishing: false,
    resolvingMedia: false,
    pinterestBoards: [],
    publishForm: { board_id: '', link_url: '' },
    activeItem: null,
    mediaMatch: null,
    selectedFiles: [],
    viewMode: 'month',
    filters: {
      networkId: '',
      date: new Date().toISOString().slice(0, 10),
      month: new Date().toISOString().slice(0, 7),
    },
    generateForm: {
      prompt_template_id: '',
      user_notes: '',
    },
    aiProviderLabel: 'AI',
  }),
  computed: {
    totalDays() {
      return this.scheduleDays.length
    },
    totalPins() {
      return this.scheduleDays.reduce((n, d) => n + (d.content_items?.length ?? 0), 0)
    },
  },
  mounted() {
    if (this.$route.query.network_id) {
      this.filters.networkId = String(this.$route.query.network_id)
    }
    this.loadAiSettings()
    this.loadNetworks().then(() => this.load())
  },
  methods: {
    async loadAiSettings() {
      try {
        const { data } = await axios.get('/api/v1/smm/ai-settings')
        this.aiProviderLabel = data.provider_label || 'AI'
      } catch {
        this.aiProviderLabel = 'AI'
      }
    },
    async loadNetworks() {
      const { data } = await axios.get('/api/v1/smm/social-networks')
      this.networks = data.data
    },
    async load() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.networkId) params.social_network_id = this.filters.networkId
        if (this.viewMode === 'day') {
          params.date = this.filters.date
        } else {
          const [y, m] = this.filters.month.split('-')
          params.year = y
          params.month = m
        }
        const { data } = await axios.get('/api/v1/smm/schedule-days', { params })
        this.scheduleDays = data.data
      } finally {
        this.loading = false
      }
    },
    pinWord(n) {
      return this.$tc('smm.schedule.pin', n, { n })
    },
    formatDateLabel(d) {
      if (!d) return ''
      const s = String(d).slice(0, 10)
      return new Date(s + 'T12:00:00').toLocaleDateString(this.$i18n.locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
      })
    },
    statusLabel(s) {
      const path = `smm.status.${s}`
      return this.$te(path) ? this.$t(path) : s
    },
    statusClass(s) {
      const m = {
        todo: 'bg-yellow-100 text-yellow-800',
        in_progress: 'bg-blue-100 text-blue-800',
        review: 'bg-purple-100 text-purple-800',
        done: 'bg-green-100 text-green-800',
      }
      return m[s] || 'bg-gray-100 text-gray-600'
    },
    async openItem(item) {
      if (!item?.id) {
        alert(this.$t('smm.schedule.slot_error'))
        return
      }

      this.mediaMatch = null
      this.generateForm = { prompt_template_id: '', user_notes: '' }
      this.publishForm = { board_id: '', link_url: item.link_url || '' }
      this.selectedFiles = []
      this.postPrompts = []
      this.pinterestBoards = []

      try {
        const { data } = await axios.get(`/api/v1/smm/content-items/${item.id}`)
        this.activeItem = data.data
        this.publishForm = { board_id: '', link_url: data.data.link_url || '' }

        const networkId = data.data.social_network_id || item.social_network_id
        if (networkId) {
          try {
            const net = await axios.get(`/api/v1/smm/social-networks/${networkId}`)
            this.postPrompts = (net.data.data.prompt_templates || []).filter(
              (p) => p.kind !== 'schedule' && p.slug !== 'prefix'
            )
            if (net.data.data.slug === 'pinterest') {
              try {
                const boards = await axios.get(
                  `/api/v1/smm/social-networks/${networkId}/pinterest/boards`
                )
                this.pinterestBoards = boards.data.data
              } catch {
                this.pinterestBoards = []
              }
            }
          } catch {
            this.postPrompts = []
            this.pinterestBoards = []
          }
        }

        this.resolveMedia(false)
      } catch (e) {
        this.activeItem = null
        alert(this.formatValidationError(e) || this.$t('smm.schedule.open_error'))
      }
    },
    async resolveMedia(force = false) {
      if (!this.activeItem) return
      this.resolvingMedia = true
      try {
        const { data } = await axios.post(
          `/api/v1/smm/content-items/${this.activeItem.id}/resolve-media`,
          {},
          { params: force ? { force: 1 } : {} }
        )
        this.activeItem = data.content_item
        this.mediaMatch = data.match
        if (data.match?.link) {
          this.publishForm = { ...this.publishForm, link_url: data.match.link }
        } else if (this.activeItem.link_url) {
          this.publishForm = { ...this.publishForm, link_url: this.activeItem.link_url }
        }
      } catch (e) {
        alert(e.response?.data?.message || this.$t('smm.schedule.resolve_error'))
      } finally {
        this.resolvingMedia = false
      }
    },
    async toggleMediaFile(fileId) {
      if (!this.activeItem || !this.mediaMatch) return

      let ids = [...(this.mediaMatch.selected_file_ids || [])]
      if (!ids.length && this.mediaMatch.selected_file_id) {
        ids = [this.mediaMatch.selected_file_id]
      }

      const normalizedId = Number(fileId)
      if (ids.some((id) => Number(id) === normalizedId)) {
        ids = ids.filter((id) => Number(id) !== normalizedId)
      } else {
        if (ids.length >= 5) {
          alert(this.$t('smm.schedule.max_images'))
          return
        }
        ids.push(normalizedId)
      }

      if (ids.length === 0) {
        alert(this.$t('smm.schedule.min_images'))
        return
      }

      try {
        const { data } = await axios.patch(
          `/api/v1/smm/content-items/${this.activeItem.id}/media-selection`,
          { media_content_file_ids: ids }
        )
        this.activeItem = data.content_item
        this.mediaMatch = data.match
      } catch (e) {
        alert(e.response?.data?.message || this.$t('smm.schedule.files_update_error'))
      }
    },
    closeItem() {
      this.activeItem = null
      this.mediaMatch = null
      this.load()
    },
    onFiles(e) {
      this.selectedFiles = Array.from(e.target.files || [])
    },
    async generateContent() {
      this.generating = true
      try {
        const fd = new FormData()
        fd.append('prompt_template_id', this.generateForm.prompt_template_id)
        if (this.generateForm.user_notes) fd.append('user_notes', this.generateForm.user_notes)
        this.selectedFiles.forEach((f, i) => {
          if (f && f.size > 0) fd.append(`images[${i}]`, f)
        })
        const { data } = await axios.post(`/api/v1/smm/content-items/${this.activeItem.id}/generate`, fd)
        this.activeItem = data.data
      } catch (e) {
        alert(this.formatValidationError(e) || e.message || this.$t('common.error'))
      } finally {
        this.generating = false
      }
    },
    formatValidationError(e) {
      const data = e.response?.data
      if (!data) return null
      if (data.message) return data.message
      if (data.errors) {
        return Object.values(data.errors).flat().join('\n')
      }
      return null
    },
    async publishPinterest() {
      if (!this.activeItem?.social_network_id) return
      this.publishing = true
      try {
        const payload = {}
        if (this.publishForm.board_id) payload.board_id = this.publishForm.board_id
        if (this.publishForm.link_url) payload.link_url = this.publishForm.link_url
        const { data } = await axios.post(
          `/api/v1/smm/social-networks/${this.activeItem.social_network_id}/pinterest/publish/${this.activeItem.id}`,
          payload
        )
        this.activeItem = data.data
        alert(this.$t('smm.schedule.pin_published'))
      } catch (e) {
        alert(e.response?.data?.message || this.$t('smm.schedule.publish_error'))
      } finally {
        this.publishing = false
      }
    },
  },
}
</script>
